import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * A proportion bar: one track, one or more segments, each with its own hover
 * tooltip and its own direct label underneath.
 *
 * Used instead of a two-slice pie. A ratio against a whole is a meter; a pie of
 * two slices is the classic way to spend a chart on a single number.
 *
 * Segments are separated by a 2px gap of the surface colour so adjacent fills
 * never touch, and every segment is labelled — colour is never the only channel
 * carrying which slice is which.
 */
const Meter = ({ segments = [], total, className }) => {
  const sum = total ?? segments.reduce((acc, s) => acc + (s.value || 0), 0);
  const visible = segments.filter((s) => s.value > 0);

  return (
    <div className={cn("w-full space-y-2", className)}>
      <div
        className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-muted"
        role="img"
        aria-label={visible.map((s) => `${s.label}: ${s.value}`).join(", ")}
      >
        {visible.map((s) => (
          <div
            key={s.label}
            title={`${s.label}: ${s.value}`}
            className={cn("h-full transition-[width] duration-300", s.className)}
            style={{ width: sum > 0 ? `${(s.value / sum) * 100}%` : 0 }}
          />
        ))}
      </div>

      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
        {segments.map((s) => (
          <span key={s.label} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span aria-hidden="true" className={cn("size-2 rounded-full", s.className)} />
            {s.label}
            <span className="font-semibold text-foreground">{s.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export { Meter };
